import { defineStore } from "pinia";
import { ref } from "vue";
import { authRepository } from "../../data/repositories/authRepository";
import { useSavedAccountStore } from "./savedAccountStore";

export type OtpMethod = "email" | "whatsapp";

const STORAGE_KEY_PREFIX = "mfa_otp_expires_";

export const useOtpVerifyStore = defineStore("otpVerifyStore", () => {
  const savedAccountStore = useSavedAccountStore();

  const isLoading = ref<boolean>(false);
  const errorMessage = ref<string | null>(null);
  const clientName = ref<string>("");
  const clientDescription = ref<string>("");
  const clientLogo = ref<string | null>(null);
  const isClientValid = ref<boolean>(true);

  const mfaToken = ref<string>("");
  const method = ref<OtpMethod>("email");
  const clientId = ref<string>("");
  const redirectUri = ref<string>("");

  // State untuk PKCE
  const codeChallenge = ref<string>("");
  const codeChallengeMethod = ref<string>("S256");

  const isSubmitting = ref<boolean>(false);
  const isResending = ref<boolean>(false);
  const submitErrorMessage = ref<string | null>(null);

  const countdown = ref<number>(0);
  let timer: any = null;

  function startCountdown(durationInSeconds: number) {
    clearInterval(timer);
    countdown.value = durationInSeconds;

    timer = setInterval(() => {
      if (countdown.value > 0) {
        countdown.value--;
      } else {
        clearInterval(timer);
        if (mfaToken.value) {
          localStorage.removeItem(STORAGE_KEY_PREFIX + mfaToken.value);
        }
      }
    }, 1000);
  }

  function initCountdown(token: string, defaultDuration = 180) {
    const storageKey = STORAGE_KEY_PREFIX + token;
    const savedExpiresAt = localStorage.getItem(storageKey);
    const now = Date.now();

    let remainingSeconds = defaultDuration;

    if (savedExpiresAt) {
      const expiresAt = parseInt(savedExpiresAt, 10);
      const diffSec = Math.floor((expiresAt - now) / 1000);
      if (diffSec > 0) {
        remainingSeconds = diffSec;
      } else {
        localStorage.removeItem(storageKey);
        remainingSeconds = 0;
      }
    } else {
      const expiresAt = now + defaultDuration * 1000;
      localStorage.setItem(storageKey, expiresAt.toString());
    }

    startCountdown(remainingSeconds);
  }

  async function validateClient(cId: string, rUri: string) {
    isLoading.value = true;
    try {
      const response = await authRepository.validateClient(cId, rUri);
      isClientValid.value = response.isValid;
      clientName.value = response.client.name;
      clientDescription.value = response.client.description ?? "";
      clientLogo.value = response.client.logo || null;
    } catch (error: any) {
      isClientValid.value = false;
      errorMessage.value = error.response?.data?.message || "Gagal memvalidasi klien.";
    } finally {
      isLoading.value = false;
    }
  }

  function init(token: string, selectedMethod: OtpMethod, cId: string, rUri: string, challenge: string = "", challengeMethod: string = "S256") {
    mfaToken.value = token;
    method.value = selectedMethod;
    clientId.value = cId;
    redirectUri.value = rUri;
    codeChallenge.value = challenge;
    codeChallengeMethod.value = challengeMethod || "S256";
    submitErrorMessage.value = null;
    isSubmitting.value = false;
    isResending.value = false;

    initCountdown(token, 180);
  }

  async function verifyOtp(code: string, onSuccess?: (redirectUrl?: string) => void) {
    if (!mfaToken.value) {
      submitErrorMessage.value = "Sesi OTP tidak ditemukan. Silakan login ulang.";
      return;
    }

    if (!code || code.length < 6) {
      submitErrorMessage.value = "Masukkan 6 digit kode OTP yang valid.";
      return;
    }

    isSubmitting.value = true;
    submitErrorMessage.value = null;

    try {
      const response = await authRepository.verifyOtp({
        mfaToken: mfaToken.value,
        otp: code,
        clientId: clientId.value,
        redirectUri: redirectUri.value,
        codeChallenge: codeChallenge.value,
        codeChallengeMethod: codeChallengeMethod.value,
      });

      clearInterval(timer);
      localStorage.removeItem(STORAGE_KEY_PREFIX + mfaToken.value);

      // Simpan akun ke savedAccountStore jika data user dikembalikan oleh backend
      if (response?.user) {
        savedAccountStore.saveAccount(response.user);
      }

      const finalRedirectUrl = response?.redirectUrl || `${redirectUri.value}?code=${response.code}`;
      onSuccess?.(finalRedirectUrl);
    } catch (error: any) {
      submitErrorMessage.value = error.response?.data?.message || "Kode OTP salah atau sudah kedaluwarsa.";
      window.$message?.warning(submitErrorMessage.value!);
    } finally {
      isSubmitting.value = false;
    }
  }

  async function resendOtp() {
    if (!mfaToken.value) {
      window.$message?.warning("Sesi OTP tidak ditemukan.");
      return;
    }

    isResending.value = true;
    submitErrorMessage.value = null;

    try {
      await authRepository.requestOtp({
        mfaToken: mfaToken.value,
        method: method.value,
      });
      window.$message?.success("Kode OTP baru telah dikirim.");

      const expiresAt = Date.now() + 180 * 1000;
      localStorage.setItem(STORAGE_KEY_PREFIX + mfaToken.value, expiresAt.toString());
      startCountdown(180);
    } catch (error: any) {
      const msg = error.response?.data?.message || "Gagal mengirim ulang kode OTP.";
      window.$message?.warning(msg);
    } finally {
      isResending.value = false;
    }
  }

  return {
    isLoading,
    errorMessage,
    clientName,
    clientDescription,
    clientLogo,
    isClientValid,
    mfaToken,
    method,
    clientId,
    redirectUri,
    codeChallenge,
    codeChallengeMethod,
    isSubmitting,
    isResending,
    submitErrorMessage,
    countdown,
    validateClient,
    init,
    verifyOtp,
    resendOtp,
  };
});
